/**
 * WebsiteSettings — visual editor for the public marketing site.
 *
 * The marketing site (Home / About / Services / Contact under /home, /about,
 * /features, /contact) is rendered entirely from a single JSON document.
 * This page lets authorised users edit that document organised the way an
 * admin actually thinks about it: by *page*.
 *
 * Layout
 * ─────────────────────────────────────────────────────────────────────────
 *   Left rail (sidebar) → groups of pages and site-wide settings:
 *
 *     PAGES
 *       🏠 Home page
 *           ├ Hero, Trust Bar, About snippet, Services, How It Works,
 *           │ Why Us, Testimonials, Service Area, Insurance, FAQ, CTA Band
 *           └ Section order (which bands appear, in what order)
 *       📖 About page          (long-form copy + stats)
 *       ⭐ Services page       (full service catalogue)
 *       📨 Contact page        (channels + form labels)
 *
 *     SITE-WIDE
 *       🏷️ Brand               (name, logo, tagline)
 *       🎨 Theme                (colors)
 *       🧭 Navigation           (top nav links + CTAs)
 *       📐 Footer               (footer columns + legal)
 *       🔍 SEO / Meta          (page titles, meta description, favicon)
 *       💬 UI Strings          (form labels, fallback text)
 *
 *   Right pane → the section editor for the selected entry.  Each entry
 *   inside the Home page expands to show all of its child blocks in a
 *   single scrolling form so admins can edit the whole page in context
 *   instead of hunting tab-by-tab.
 *
 * Architecture
 *   1. On mount we fetch the current tenant's config row.
 *   2. The payload is held in state.  Section editors mutate slices via a
 *      `setSection` setter — every keystroke updates the parent's draft.
 *   3. "Publish" calls ssMarketingAPI.updateSiteConfig(payload, version);
 *      a 409 surfaces a clear conflict dialog with a Reload button.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { ssMarketingAPI } from '../../../services/ssApi';
import { useAuth } from '../../../context/AuthContext';
import { useRbac } from '../../../context/RbacContext';
import { invalidateSiteConfig } from '../../marketing/useSiteConfig';
import {
  PageHeader, Card, Button, Spinner,
  ErrorBanner, SuccessBanner, InfoBanner, Badge,
  COLOR, FONT,
} from '../../../components/ss';

import HeroEditor from './HeroEditor';
import StubEditor from './StubEditor';

// ── Navigation catalogue ──────────────────────────────────────────────────
//
// Each entry can be one of:
//   • `kind: 'section'`   — a single config slice; renders one editor.
//   • `kind: 'composite'` — a list of slices belonging to the same page;
//                           renders each slice's editor stacked vertically.
//
// `groups` is the left-rail structure.  We keep page entries first because
// admins almost always come here to "edit the home page" or "fix the
// contact page" — site-wide stuff is the long tail.

const NAV_GROUPS = [
  {
    label: 'Pages',
    entries: [
      {
        id: 'page.home',
        label: 'Home page',
        icon: '🏠',
        hint: 'The marketing landing page at /home — every band shown to a visitor.',
        kind: 'composite',
        sections: [
          { id: 'hero',         label: 'Hero band',         configKey: 'hero',         component: 'hero' },
          { id: 'trustBar',     label: 'Trust bar',         configKey: 'trustBar',     component: 'stub' },
          { id: 'about',        label: 'About snippet',     configKey: 'about',        component: 'stub',
            note: 'The long-form About page also reads from this block (eyebrow / title / body / stats).' },
          { id: 'features',     label: 'Services band',     configKey: 'features',     component: 'stub',
            note: 'The /features page is fully driven by this same block.' },
          { id: 'howItWorks',   label: 'How it works',      configKey: 'howItWorks',   component: 'stub' },
          { id: 'whyUs',        label: 'Why us',            configKey: 'whyUs',        component: 'stub' },
          { id: 'testimonials', label: 'Testimonials',      configKey: 'testimonials', component: 'stub' },
          { id: 'serviceArea',  label: 'Service area',      configKey: 'serviceArea',  component: 'stub' },
          { id: 'insurance',    label: 'Insurance',         configKey: 'insurance',    component: 'stub' },
          { id: 'faq',          label: 'FAQ',               configKey: 'faq',          component: 'stub' },
          { id: 'ctaBand',      label: 'Final CTA band',    configKey: 'ctaBand',      component: 'stub' },
          { id: 'homeOrder',    label: 'Section order',     configKey: 'homeOrder',    component: 'stub',
            note: 'Order of bands on the home page. Drop a name to hide it; reorder to rearrange.' },
        ],
      },
      {
        id: 'page.about',
        label: 'About page',
        icon: '📖',
        hint: 'Long-form mission page at /about. Reads `about.body` and `about.stats`.',
        kind: 'section',
        configKey: 'about',
        component: 'stub',
        note: 'Same data block as the Home About snippet — edits here update both pages.',
      },
      {
        id: 'page.services',
        label: 'Services page',
        icon: '⭐',
        hint: 'Full service catalogue at /features.',
        kind: 'section',
        configKey: 'features',
        component: 'stub',
        note: 'Same data block as the Home Services band — edits here update both pages.',
      },
      {
        id: 'page.contact',
        label: 'Contact page',
        icon: '📨',
        hint: 'Contact page at /contact — channels and lead-capture form.',
        kind: 'section',
        configKey: 'contact',
        component: 'stub',
      },
    ],
  },
  {
    label: 'Site-wide',
    entries: [
      { id: 'brand',  label: 'Brand',        icon: '🏷️', kind: 'section', configKey: 'brand',  component: 'stub',
        hint: 'Brand name, logo, tagline. Shown in the top nav, footer, and meta tags.' },
      { id: 'theme',  label: 'Theme',        icon: '🎨', kind: 'section', configKey: 'theme',  component: 'stub',
        hint: 'Brand colors and gradients used across every page.' },
      { id: 'nav',    label: 'Navigation',   icon: '🧭', kind: 'section', configKey: 'nav',    component: 'stub',
        hint: 'Top-of-page navigation links and the two CTA buttons.' },
      { id: 'footer', label: 'Footer',       icon: '📐', kind: 'section', configKey: 'footer', component: 'stub',
        hint: 'Footer link columns, legal/copyright text, and social icons.' },
      { id: 'meta',   label: 'SEO / Meta',   icon: '🔍', kind: 'section', configKey: 'meta',   component: 'stub',
        hint: 'Per-page <title>, default meta description, and favicon URL.' },
      { id: 'ui',     label: 'UI strings',   icon: '💬', kind: 'section', configKey: 'ui',     component: 'stub',
        hint: 'Form labels, button text, and fallback strings used across the site.' },
    ],
  },
];

const fmtErr = (e) => {
  const detail = e?.response?.data?.detail;
  if (detail?.message) return detail.message;
  if (typeof detail === 'string' && detail) return detail;
  // FastAPI validation errors are arrays of objects — never return them raw.
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return e?.message || 'Request failed';
};

// Default landing entry — Home page.
const DEFAULT_ENTRY_ID = 'page.home';

// Look up an entry by id across all groups.
function findEntry(id) {
  for (const g of NAV_GROUPS) {
    for (const e of g.entries) if (e.id === id) return e;
  }
  return null;
}

export default function WebsiteSettings() {
  const { user } = useAuth();
  const { can, loading: rbacLoading } = useRbac() || {};
  // Fail closed: while the RBAC manifest is still loading we deny write so a
  // slow/failed permission load can never transiently expose write controls.
  // Once loaded we trust the manifest (the server still enforces the gate).
  const canWrite = rbacLoading
    ? false
    : (typeof can === 'function' ? can('marketing.config.write') : false);

  const [entryId, setEntryId]     = useState(DEFAULT_ENTRY_ID);
  const [version, setVersion]     = useState(null);
  const [payload, setPayload]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState('');
  const [ok, setOk]               = useState('');
  const [conflict, setConflict]   = useState(null);
  const [dirty, setDirty]         = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(''); setOk(''); setConflict(null);
    try {
      const r = await ssMarketingAPI.getSiteConfig();
      setPayload(r?.payload || {});
      setVersion(typeof r?.version === 'number' ? r.version : 1);
      setDirty(false);
    } catch (e) {
      setErr(fmtErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setSection = useCallback((key, next) => {
    setPayload((prev) => {
      const before = prev?.[key];
      const after  = typeof next === 'function' ? next(before) : next;
      if (after === before) return prev;
      setDirty(true);
      return { ...(prev || {}), [key]: after };
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!payload || !canWrite) return;
    setSaving(true); setErr(''); setOk(''); setConflict(null);
    try {
      const r = await ssMarketingAPI.updateSiteConfig(payload, version);
      setPayload(r?.payload || payload);
      setVersion(typeof r?.version === 'number' ? r.version : (version || 1) + 1);
      setOk(`Published v${r?.version} — changes are now live.`);
      setDirty(false);
      // Bust the marketing site's module-level cache so the Preview tab
      // (and any /home /about /features /contact in this browser session)
      // picks up the new payload on its next render.
      try { invalidateSiteConfig(); } catch (_) { /* non-fatal */ }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        const detail = e?.response?.data?.detail || {};
        setConflict({
          message: detail.message || 'Site config has been updated by someone else.',
          currentVersion: detail.current_version,
          yourVersion: detail.your_version,
        });
      } else {
        setErr(fmtErr(e));
      }
    } finally {
      setSaving(false);
    }
  }, [payload, version, canWrite]);

  const handlePreview = (path = '/home') => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const entry = useMemo(() => findEntry(entryId) || findEntry(DEFAULT_ENTRY_ID), [entryId]);

  // The preview link follows the page being edited so admins can jump
  // straight to the version of the site they're working on.
  const previewPath = entry?.id === 'page.about'    ? '/about'
                    : entry?.id === 'page.services' ? '/features'
                    : entry?.id === 'page.contact'  ? '/contact'
                    : '/home';

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={{ fontFamily: FONT.family, padding: '0 0 60px' }}>
      <PageHeader
        title="Website Settings"
        subtitle="Edit every word, link, and image on your public marketing site — page by page."
        action={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button variant="ghost" onClick={() => handlePreview(previewPath)}>
              ↗ Preview {previewPath}
            </Button>
            <Button variant="ghost" onClick={load} disabled={loading || saving}>
              {loading ? '…' : '↻ Reload'}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!canWrite || !dirty || saving || loading || !payload}
              title={
                !canWrite ? 'You need the marketing.config.write permission'
                : !dirty   ? 'No unsaved changes'
                : 'Publish current draft to the live site'
              }
            >
              {saving ? 'Publishing…' : (dirty ? 'Publish changes' : 'Published')}
            </Button>
          </div>
        }
      />

      {/* Header bar — version + dirty + permission */}
      <Card style={{ padding: '10px 14px', marginBottom: 16,
                     display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: COLOR.textMuted }}>Version</span>
          <Badge color="gray">{version != null ? `v${version}` : '—'}</Badge>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: COLOR.textMuted }}>Status</span>
          {dirty
            ? <Badge color="amber">Unsaved changes</Badge>
            : <Badge color="green">Up to date</Badge>}
        </div>
        {!canWrite && <Badge color="gray">Read-only — needs marketing.config.write</Badge>}
        {user?.username && (
          <span style={{ marginLeft: 'auto', fontSize: 12, color: COLOR.textMuted }}>
            Signed in as <b>{user.username}</b>
          </span>
        )}
      </Card>

      {err && <ErrorBanner style={{ marginBottom: 12 }}>{err}</ErrorBanner>}
      {ok  && <SuccessBanner style={{ marginBottom: 12 }}>{ok}</SuccessBanner>}
      {conflict && (
        <ErrorBanner style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 8 }}>
            <b>Version conflict.</b> {conflict.message}
            {conflict.currentVersion != null && (
              <> Server version is <b>v{conflict.currentVersion}</b>; your draft was based on <b>v{conflict.yourVersion}</b>.</>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="sm" onClick={load}>Reload latest</Button>
            <Button size="sm" variant="ghost" onClick={() => setConflict(null)}>Dismiss</Button>
          </div>
        </ErrorBanner>
      )}

      {/* Two-column layout: nav rail + editor */}
      {loading && <Spinner />}
      {!loading && payload && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 260px) minmax(0, 1fr)',
          gap: 18,
          alignItems: 'flex-start',
        }}>
          <NavRail
            groups={NAV_GROUPS}
            activeId={entry?.id}
            onSelect={setEntryId}
            payload={payload}
          />
          <div style={{ minWidth: 0 }}>
            <EntryEditor entry={entry} payload={payload} setSection={setSection} />
          </div>
        </div>
      )}

      {!loading && !payload && !err && (
        <InfoBanner>No config payload returned. Try Reload.</InfoBanner>
      )}
    </div>
  );
}

// ── Left navigation rail ────────────────────────────────────────────────────
function NavRail({ groups, activeId, onSelect, payload }) {
  return (
    <Card style={{ padding: 8, position: 'sticky', top: 12 }}>
      {groups.map((grp) => (
        <div key={grp.label} style={{ marginBottom: 10 }}>
          <div style={{
            padding: '6px 10px 4px', fontSize: 10, fontWeight: 800,
            color: COLOR.textMuted, textTransform: 'uppercase', letterSpacing: '.8px',
          }}>
            {grp.label}
          </div>
          {grp.entries.map((e) => {
            const isActive = activeId === e.id;
            const sliceCount = e.kind === 'composite'
              ? (e.sections || []).filter(s => payload?.[s.configKey] != null).length
              : null;
            return (
              <button
                key={e.id}
                onClick={() => onSelect(e.id)}
                style={{
                  width: '100%', textAlign: 'left',
                  padding: '8px 10px',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  background: isActive ? COLOR.blueBg : 'transparent',
                  color: isActive ? COLOR.text : COLOR.text,
                  fontSize: 13, fontWeight: isActive ? 700 : 500,
                  fontFamily: FONT.family,
                  display: 'flex', alignItems: 'center', gap: 8,
                  marginBottom: 2,
                  borderLeft: `3px solid ${isActive ? COLOR.blue : 'transparent'}`,
                }}
                onMouseEnter={(ev) => { if (!isActive) ev.currentTarget.style.background = COLOR.grayBg; }}
                onMouseLeave={(ev) => { if (!isActive) ev.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: 15, width: 18, textAlign: 'center' }}>{e.icon}</span>
                <span style={{ flex: 1, minWidth: 0 }}>{e.label}</span>
                {sliceCount != null && (
                  <Badge color="gray" style={{ fontSize: 10 }}>{sliceCount}</Badge>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </Card>
  );
}

// ── Right pane — renders one entry (single section or a composite page) ────
function EntryEditor({ entry, payload, setSection }) {
  if (!entry) return null;

  // Helper to render a single section (one configKey).
  const renderSection = (s, opts = {}) => {
    const slice = payload[s.configKey];
    const onChange = (next) => setSection(s.configKey, next);
    if (s.component === 'hero') {
      return <HeroEditor value={slice} onChange={onChange} />;
    }
    return <StubEditor sectionKey={s.configKey} value={slice} onChange={onChange} />;
  };

  if (entry.kind === 'composite') {
    return (
      <div>
        {/* Page header banner — explains scope of this group of sections */}
        <Card style={{ padding: '14px 16px', marginBottom: 14,
                       background: 'linear-gradient(180deg, #f3f8ff 0%, #ffffff 100%)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22 }}>{entry.icon}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: COLOR.text }}>{entry.label}</div>
              {entry.hint && (
                <div style={{ fontSize: 13, color: COLOR.textMuted, marginTop: 2 }}>{entry.hint}</div>
              )}
            </div>
          </div>
        </Card>

        {/* Sub-section anchor strip — quick jump within this page */}
        <Card style={{ padding: 8, marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {(entry.sections || []).map((s) => (
            <a key={s.id} href={`#sec-${s.id}`}
               style={{
                 padding: '4px 10px', borderRadius: 999,
                 background: COLOR.grayBg, color: COLOR.text,
                 fontSize: 12, fontWeight: 600, textDecoration: 'none',
                 border: `1px solid ${COLOR.graySubtle}`,
               }}>
              {s.label}
            </a>
          ))}
        </Card>

        {(entry.sections || []).map((s) => (
          <div id={`sec-${s.id}`} key={s.id} style={{ marginBottom: 24, scrollMarginTop: 80 }}>
            {s.note && (
              <InfoBanner style={{ marginBottom: 8 }}>{s.note}</InfoBanner>
            )}
            {renderSection(s)}
          </div>
        ))}
      </div>
    );
  }

  // Single-section entry (About / Services / Contact / Site-wide blocks).
  return (
    <div>
      <Card style={{ padding: '14px 16px', marginBottom: 14,
                     background: 'linear-gradient(180deg, #f3f8ff 0%, #ffffff 100%)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>{entry.icon}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: COLOR.text }}>{entry.label}</div>
            {entry.hint && (
              <div style={{ fontSize: 13, color: COLOR.textMuted, marginTop: 2 }}>{entry.hint}</div>
            )}
          </div>
        </div>
      </Card>

      {entry.note && <InfoBanner style={{ marginBottom: 12 }}>{entry.note}</InfoBanner>}
      {renderSection(entry)}
    </div>
  );
}
